/**
 * Type guards narrowing arbitrary `unknown` (the port-level shape of
 * `Article.metadata`) to `LakikaveriInfoMetadata` for the
 * `LakikaveriInfoRoute` (TA20.2).
 *
 * Why guards, not casts
 * =====================
 *
 * `Article.metadata` is `Record<string, unknown>` at the port
 * boundary by design (see `ContentReaderPort.ts`'s JSDoc). The seed
 * is hand-authored and trusted at write time, but the runtime guard
 * preserves safety against any future adapter that surfaces a wrong
 * shape (a CMS adapter, a misformed fixture, etc.). A failed guard
 * triggers the route's `throw` → the caller-mounted
 * `PortErrorBoundary` surfaces the diagnostic.
 *
 * Why a separate file
 * ===================
 *
 * The route's metadata is the most structurally rich of any Phase A
 * article: a four-element `steps` array where each element optionally
 * carries three sub-arrays (`actions`, `documents`, `internalLinks`),
 * each with their own per-element shape. Walking the structure
 * inline would push the route over the 400-line FR-045 ceiling and
 * blur the route's composition surface. Extracting to a colocated
 * helper module keeps the route file focused on rendering and lets
 * the guards be tested through the route's "metadata violation
 * throws" tests without exporting a per-guard test surface.
 *
 * Module-local guards
 * ===================
 *
 * The four sub-guards (`isLakikaveriStepAction`,
 * `isLakikaveriStepDocument`, `isLakikaveriStepInternalLink`,
 * `isLakikaveriStep`) are NOT exported — they are implementation
 * detail of `isLakikaveriInfoMetadata`. Only the top-level guard is
 * exported.
 *
 * Route-agnostic helpers (`isOutboundLink`, `isReadonlyArrayOf<T>`)
 * live in `contentMetadataGuards.ts`.
 *
 * Discriminator update protocol
 * =============================
 *
 * The `LakikaveriStepAction` discriminator currently has one variant
 * (`'openContactModal'`). When a future seed introduces a new action
 * (e.g. `'openCalculator'`), the protocol per
 * `phase-a-content-inventory.md` §"Vocabulary pin" is:
 *
 *   1. Extend `LakikaveriStepAction` in
 *      `packages/shared-types/src/content-metadata.ts`.
 *   2. Update `isLakikaveriStepAction` here so the guard recognises
 *      the new literal.
 *   3. Update `LakikaveriStepsList` to render the new control.
 *   4. THEN write the new action into seed JSONB.
 *
 * Seed-first ordering would silently break the guard at runtime; the
 * type-first ordering above keeps the typecheck honest.
 */

import type {
  LakikaveriInfoMetadata,
  LakikaveriStep,
  LakikaveriStepAction,
  LakikaveriStepDocument,
  LakikaveriStepInternalLink,
} from '@omaplus/shared-types/content-metadata';

import { isOutboundLink, isReadonlyArrayOf } from './contentMetadataGuards.js';

/**
 * The closed set of `LakikaveriStepAction` literals. Single source of
 * truth so adding a new action is a one-line change at this constant
 * + the `LakikaveriStepAction` type union; the guard inherits the new
 * variant automatically.
 */
const KNOWN_STEP_ACTIONS: readonly LakikaveriStepAction[] = ['openContactModal'];

function isLakikaveriStepAction(value: unknown): value is LakikaveriStepAction {
  return typeof value === 'string' && (KNOWN_STEP_ACTIONS as readonly string[]).includes(value);
}

function isLakikaveriStepDocument(value: unknown): value is LakikaveriStepDocument {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return typeof record['titleFi'] === 'string' && typeof record['externalUrl'] === 'string';
}

function isLakikaveriStepInternalLink(value: unknown): value is LakikaveriStepInternalLink {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return typeof record['labelFi'] === 'string' && typeof record['path'] === 'string';
}

function isLakikaveriStep(value: unknown): value is LakikaveriStep {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  if (
    typeof record['number'] !== 'number' ||
    !Number.isFinite(record['number']) ||
    typeof record['titleFi'] !== 'string' ||
    typeof record['bodyHtml'] !== 'string'
  ) {
    return false;
  }
  // The three `?` ReadonlyArray fields each must EITHER be absent
  // (key missing OR explicitly `undefined`) OR be a valid array of
  // the corresponding shape. A field that is present but a non-array
  // (or an array with a wrong-shape entry) is a contract violation.
  if (
    record['actions'] !== undefined &&
    !isReadonlyArrayOf(record['actions'], isLakikaveriStepAction)
  ) {
    return false;
  }
  if (
    record['documents'] !== undefined &&
    !isReadonlyArrayOf(record['documents'], isLakikaveriStepDocument)
  ) {
    return false;
  }
  if (
    record['internalLinks'] !== undefined &&
    !isReadonlyArrayOf(record['internalLinks'], isLakikaveriStepInternalLink)
  ) {
    return false;
  }
  return true;
}

/**
 * Top-level guard: narrow `unknown` to `LakikaveriInfoMetadata`.
 * Validates the required `steps` array and optional authored values.
 *
 * Takes `unknown` (not `Record<string, unknown>`) because TS type
 * predicates require the predicate's type to be assignable to the
 * parameter's type, and the metadata interface uses `readonly`
 * properties that are NOT assignable to `Record<string, unknown>`'s
 * index signature.
 */
export function isLakikaveriInfoMetadata(value: unknown): value is LakikaveriInfoMetadata {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  if (!isReadonlyArrayOf(record['steps'], isLakikaveriStep)) {
    return false;
  }
  return (
    (record['outboundLink'] === undefined || isOutboundLink(record['outboundLink'])) &&
    (record['ctaLabelFi'] === undefined || typeof record['ctaLabelFi'] === 'string')
  );
}
